import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { answerQuestion, clearSelectedCertificationCli, setTimeRemaining, RootState } from "../../store";
import CountDown from "../../components/Quiz/CountDown";
import BackToExamModal from "../../components/Modal/BackToExamModal";
import QuizSidebar from "../../components/Quiz/QuizSidebar";
import { setScore, setUnattemptedQuestions } from "../../store";
import axios from 'axios';
import { baseUrl } from "../../constants/api";
import showToastMessage from "../../utils/showToastMessage";

import correctSound from '../../assets/audio/Correct Answer.mp3';
import incorrectSound from '../../assets/audio/Wrong Answer.mp3';
import extro from '../../assets/audio/Extro.mp3';

interface Question {
    _id: string;
    question: string;
    options: string[];
    correct_option_id: number;
    submitted: boolean;
    submittedAnswer: number | null;
}

const QuizQuestionNew = () => {
    const { questions, quizzes, selectedCertificationCli } = useSelector((state: RootState) => state.quiz);
    const { user_id, token } = useSelector((state: RootState) => state.account);
    const [selectedOption, setSelectedOption] = useState<number | null>(null);
    const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
    const [quizid, setQuizid] = useState<string | null>(null);
    const { questionId, id } = useParams<{ questionId: string; id: string }>();
    const timeRemaining = useSelector((state: RootState) => state.quiz.time_limit);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [questionIndex, setQuestionIndex] = useState(false);
    const sidebarRef = useRef<HTMLDivElement>(null);
    const [animationTriggered, setAnimationTriggered] = useState(false);
    const [isMuted] = useState(false);

    // Memoize audio objects to avoid re-creation if needed, but for now just keep them
    const correctAudio = useRef(new Audio(correctSound)).current;
    const incorrectAudio = useRef(new Audio(incorrectSound)).current;
    const extroAudio = useRef(new Audio(extro)).current;

    useEffect(() => {
        if (id) {
            setQuizid(id);
        }
    }, [id]);

    useEffect(() => {
        setAnimationTriggered(true);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
                setQuestionIndex(false); // Close sidebar if clicked outside
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const playSound = (isCorrect: boolean) => {
        if (!isMuted) {
            if (isCorrect) {
                correctAudio.play();
            } else {
                incorrectAudio.play();
            }
        }
    };

    const submitQuizReport = async (correctCount: number, qIds: string[], markedOptionsArray: (number | null)[]) => {
        const selectedQuiz = (quizzes || []).find((quiz: any) => quiz.quiz_id === quizid);
        if (selectedQuiz?.quiz_type === "certification" && (!selectedCertificationCli || selectedCertificationCli.quiz_id !== quizid)) {
            showToastMessage("Certification CLI selection is missing. Please restart the certification quiz.", "error");
            navigate("/");
            return;
        }

        const reportData = {
            user_id: user_id,
            quiz_id: quizid,
            quiz_array: [...qIds],
            marked_options_array: [...markedOptionsArray],
            score: correctCount,
            selected_certificate_cli_id: selectedQuiz?.quiz_type === "certification" ? selectedCertificationCli?.cli_id : null,
        };

        try {
            await axios.post(
                `${baseUrl}/api/submit-quiz-report`,
                reportData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            dispatch(clearSelectedCertificationCli());
        } catch (error) {
            console.error("Error submitting quiz report:", error);
        }
    };

    const getQuizStats = () => {
        let correctCount = 0;
        const markedOptionsArray: (number | null)[] = [];
        const unattemptedQuestions: string[] = [];
        const questionsList = Object.values(questions) as Question[];
        const qIds = questionsList.map((q) => q._id);

        questionsList.forEach((question) => {
            markedOptionsArray.push(question.submittedAnswer);
            if (question.submittedAnswer === question.correct_option_id) {
                correctCount++;
            } else if (question.submittedAnswer === null) {
                unattemptedQuestions.push(question._id);
            }
        });

        return {
            correctCount,
            markedOptionsArray,
            unattemptedQuestions,
            qIds,
            totalQuestions: questionsList.length,
            incorrectCount: questionsList.length - correctCount - unattemptedQuestions.length,
        };
    };

    const handleQuizEnd = async () => {
        extroAudio.play();
        const { correctCount, markedOptionsArray, unattemptedQuestions, qIds, totalQuestions, incorrectCount } = getQuizStats();

        dispatch(setScore(correctCount));
        dispatch(setUnattemptedQuestions(unattemptedQuestions));
        await submitQuizReport(correctCount, qIds, markedOptionsArray);

        navigate(`/quiz/${id}/result`, {
            replace: true,
            state: {
                correctCount,
                totalQuestions,
                incorrectCount,
                unattemptedCount: unattemptedQuestions.length,
            },
        });
    };

    useEffect(() => {
        let regex = /^[1-9]\d*$/;
        if (
            !questionId ||
            !regex.test(questionId) ||
            Number(questionId) > Object.keys(questions).length
        ) {
            navigate(-1);
            return;
        }

        setCurrentQuestion(questions[Number(questionId) - 1] as Question);
        setSelectedOption(null);
    }, [questionId, questions, navigate]);

    const handleQuesIndex = () => {
        setQuestionIndex(!questionIndex);
    };

    const handleOptionChange = (selected_option: number) => {
        setSelectedOption(selected_option);
    };

    const handleSubmit = () => {
        if (selectedOption === null || !currentQuestion) return;

        const isCorrect = selectedOption === currentQuestion.correct_option_id;
        playSound(isCorrect);

        if (questionId) {
            dispatch(
                answerQuestion({
                    questionId: Number(questionId) - 1,
                    answer: selectedOption,
                })
            );
        }
    };

    const handleNext = () => {
        if (!questionId) return;
        let nq = Number(questionId);
        if (nq < Object.keys(questions).length) {
            navigate(`/quiz/${id}/${nq + 1}`);
            setSelectedOption(null);
        }
    };

    const handleQuizTimeUp = async () => {
        const { correctCount, markedOptionsArray, unattemptedQuestions, qIds, totalQuestions, incorrectCount } = getQuizStats();
        dispatch(setTimeRemaining(0));
        dispatch(setScore(correctCount));
        dispatch(setUnattemptedQuestions(unattemptedQuestions));
        await submitQuizReport(correctCount, qIds, markedOptionsArray);
        navigate(`/quiz/${id}/result`, {
            replace: true,
            state: {
                correctCount,
                totalQuestions,
                incorrectCount,
                unattemptedCount: unattemptedQuestions.length,
            },
        });
    };

    const areAllQuestionsAttempted = (ques: Record<string, any>) => {
        const questionsList = Object.values(ques) as Question[];
        return questionsList.every((question) => question.submitted);
    };

    const getOptionClassName = (optionIndex: number) => {
        if (currentQuestion?.submitted) {
            if (optionIndex === currentQuestion.correct_option_id) {
                return "rightAnswer";
            } else if (optionIndex === currentQuestion.submittedAnswer) {
                return "wrongAnswer";
            }
        } else if (optionIndex === selectedOption) {
            return "optionSelected";
        }
        return "";
    };

    const totalQuestionsCount = Object.keys(questions).length;

    return (
        <>
            <div
                style={{
                    zIndex: 1000,
                    position: "absolute",
                    top: "14px",
                    right: "50px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <button
                    onClick={handleQuizEnd}
                    className="SubmitQuizBtn"
                    style={{
                        display: areAllQuestionsAttempted(questions) || questionIndex ? "none" : "",
                        borderRadius: "50px",
                        border: "none",
                        color: "white",
                        backgroundColor: "#EF4444",
                        zIndex: 1200,
                        fontSize: "23px",
                        fontWeight: "400",
                        padding: "2px 24px",
                        userSelect: "none",
                        transition: "border-color 0.3s ease, box-shadow 0.2s ease, color 0.2s ease",
                    }}
                    type="button"
                >
                    End Quiz
                </button>
            </div>
            <div className="QuizBackground">
                <div className={`QuestionUI ${animationTriggered ? 'animate' : ''}`} style={{ position: "relative", bottom: "50px" }}>
                    <div className="vibrantQuestionColor col-10 col-xl-11">
                        <div className="QuestionContainer" id="questionContainer">
                            <p className="QuestionText">
                                {currentQuestion?.question}
                            </p>
                        </div>
                    </div>
                    {/* Lines removed for brevity or kept if they are purely UI */}
                    <div className="d-flex flex-column w-100 align-items-center justify-center gap-3">
                        <div className={`OptionUI z-index-100 ${animationTriggered ? 'animate' : ''}`}>
                            {(() => {
                                if (!currentQuestion) return null;
                                const options = currentQuestion.options;
                                const displayOptions = options.length === 3 ? [...options, "None of the above"] : options;

                                return displayOptions.map((option, index) => (
                                    <div key={index} className="VibrantOptionColor">
                                        <div
                                            className={`optionContainer ${getOptionClassName(index)}`}
                                            onClick={() => {
                                                if (currentQuestion && !currentQuestion.submitted) {
                                                    handleOptionChange(index);
                                                }
                                            }}
                                            style={option?.length > 150 ? { height: "140px" } : {}}
                                        >
                                            <div className="optionImg col-1.5 p-2">
                                                {String.fromCharCode(65 + index)}.
                                            </div>
                                            <div className="scrollableText">
                                                <div className="optionText">
                                                    {option}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ));
                            })()}
                        </div>

                        <div className="col-10 col-xl-11 d-flex justify-content-center w-100 mt-4">
                            <div className="d-flex align-items-center justify-content-center w-100">
                                {currentQuestion?.submitted && !areAllQuestionsAttempted(questions) && (
                                    <button
                                        className="SubmitBtn NextBtn col-md-2.5"
                                        onClick={handleNext}
                                        style={{
                                            visibility: totalQuestionsCount === Number(questionId) ? "hidden" : "visible",
                                        }}
                                        type="button"
                                    >
                                        Next
                                    </button>
                                )}
                                {!currentQuestion?.submitted && (
                                    <button
                                        className="SubmitBtn col-3 col-md-2.5 col-lg-2"
                                        onClick={handleSubmit}
                                        type="button"
                                        style={{
                                            visibility: selectedOption !== null ? "visible" : "hidden",
                                        }}
                                    >
                                        Submit
                                    </button>
                                )}
                                {areAllQuestionsAttempted(questions) && (
                                    <button
                                        className="SubmitBtn col-3 col-md-2.5 col-lg-2"
                                        onClick={handleQuizEnd}
                                        type="button"
                                    >
                                        Submit Quiz
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="logoComponent"></div>
            </div>
            <div className="QuesCount" onClick={handleQuesIndex}>
                <span style={{ fontWeight: "bold" }}>Q.{questionId}</span>/{totalQuestionsCount}
            </div>
            <div className="TimeOutCount">
                <CountDown time_Left={timeRemaining} onTimeUp={handleQuizTimeUp} />
            </div>
            <BackToExamModal open={false} handleClose={() => { }} onYes={() => { }} />
            <div
                className="SidebarQuestion"
                style={questionIndex ? {} : { display: "none" }}
                ref={sidebarRef}
            >
                <QuizSidebar setSelectedOption={setSelectedOption} />
            </div>
            <div className="QuizBackgroundOverlay"></div>
        </>
    );
};

export default QuizQuestionNew;
